import { useEffect, useState } from "react";

// `gpt-token-utils` weighs ~548 KB gz and is only needed when the user is
// actively editing a character / group form (live token counting). Loading it
// eagerly on cold visits puts that weight on the critical path for every
// visitor, including those who never touch a creation form. We lazy-import it
// on mount of the first consuming component and cache the promise across the
// app so subsequent mounts are free.

type CountTokensFn = (text: string) => number;

const zeroTokensCounter: CountTokensFn = () => 0;

function buildCounter(encode: (text: string) => { tokens: number[] } | null | undefined): CountTokensFn {
  return (text: string): number => {
    if (!text) return 0;
    return encode(text)?.tokens.length ?? 0;
  };
}

let encoderPromise: Promise<CountTokensFn> | null = null;

function loadEncoder(): Promise<CountTokensFn> {
  if (!encoderPromise) {
    encoderPromise = import("gpt-token-utils")
      .then(({ encode }) => buildCounter(encode))
      .catch((error) => {
        // Clear the cached rejection so a future mount (e.g. user retries the
        // form, network recovers) can attempt the dynamic import again instead
        // of being permanently stuck at zero counts.
        encoderPromise = null;
        throw error;
      });
  }
  return encoderPromise;
}

/**
 * Returns a synchronous token-counting function plus a readiness flag.
 *
 * Until the underlying `gpt-token-utils` chunk finishes loading,
 * `countTokens` returns 0 for every input and `isReady` is `false`. Callers
 * MUST gate validation + submission on `isReady`: while counts are stubbed
 * at 0, token-limit checks cannot distinguish "empty input" from "massively
 * over-limit pre-populated content" (notably on edit forms), so treating
 * the form as valid during the load window would let users bypass the
 * limit. Once the chunk resolves, a state update re-renders consumers with
 * the real counter and `isReady` flips to `true`.
 */
type TokenCounter = {
  countTokens: CountTokensFn;
  isReady: boolean;
};

let resolvedCounter: CountTokensFn = zeroTokensCounter;
let isEncoderReady = false;

export default function useTokenCounter(): TokenCounter {
  const [stateIsReady, setStateIsReady] = useState(isEncoderReady);

  useEffect(() => {
    if (isEncoderReady) return;
    let isMounted = true;
    loadEncoder()
      .then((counter) => {
        resolvedCounter = counter;
        isEncoderReady = true;
        if (isMounted) setStateIsReady(true);
      })
      .catch(() => {
        // Swallow: token counts stay at 0 for this session and `isReady`
        // stays `false`, so consumers keep blocking submission instead of
        // silently allowing over-limit content through. Avoids an
        // unhandled-rejection warning when the chunk fails to load.
      });
    return () => {
      isMounted = false;
    };
  }, []);

  return { countTokens: resolvedCounter, isReady: stateIsReady };
}
